/**
 * Offline-aware fallback for the lazy-loaded Runbook editor (issue #216).
 *
 * The editor is deliberately online-only, so a failed chunk load while
 * offline is expected; this turns it into a readable warning with an
 * explicit retry instead of a blank route.
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/design-system";
import { Icon } from "@/components/Icon";
import { useOnline } from "@/lib/useOnline";

export interface RunbookLoadErrorProps {
  onRetry: () => void;
}

export function RunbookLoadError({ onRetry }: RunbookLoadErrorProps) {
  const { t } = useTranslation();
  const online = useOnline();

  return (
    <div
      data-testid="runbook-load-error"
      style={{
        padding: "var(--space-8) var(--space-4)",
        textAlign: "center",
        color: "var(--text-muted)",
      }}
    >
      <Icon name="compass" size={28} color="var(--text-faint)" />

      <div
        style={{
          font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
          color: "var(--text-strong)",
          marginTop: 10,
          marginBottom: 6,
        }}
      >
        {online ? t("runbooks.loadErrorTitle") : t("offline.runbookTitle")}
      </div>

      <div
        style={{
          font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
          maxWidth: "42ch",
          margin: "0 auto",
        }}
      >
        {online ? t("runbooks.loadErrorBody") : t("offline.runbookBody")}
      </div>

      <div style={{ marginTop: "var(--space-4)" }}>
        <Button onClick={onRetry} data-testid="runbook-load-retry">
          {t("offline.retry")}
        </Button>
      </div>
    </div>
  );
}
